import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle,
  BookOpen,
  Check,
  ChevronDown,
  Database,
  FileText,
  Lightbulb,
  Loader,
  Plug,
  Plus,
  RefreshCw,
  Search,
  Shield,
  Workflow,
  Wrench,
  X,
} from 'lucide-react';
import {
  CAPABILITY_ICON_OPTIONS,
  DEFAULT_CAPABILITY_ICON,
  DATA_MODEL_OPERATION_OPTIONS,
  RESOURCE_SOURCE_OPTIONS,
  RISK_OPTIONS,
  TOOL_SOURCE_OPTIONS,
  resolveCapabilityIconKey,
  SINGLETON_ENDPOINT_ID,
  buildPublishedAssetBinding,
  buildPublishedModelBinding,
  composeAssetToolName,
  isLegacyAssetPublishedTool,
  isQueryablePublishAsset,
  normalizeMcpToolName,
  publishedToolNameTaken,
  splitAssetToolName,
} from '../governedMcpConstants';
import { logGovernedMcp } from '../governedMcpLogger';
import { capabilityGroupIds, createId } from '../governedMcpStore';
import {
  CLIENT_GUIDANCE_FOLLOW_UP,
  buildDataModelSchemas,
  buildKnowledgeBaseSchemas,
  buildWorkflowSchemas,
  composeClientGuidance,
  deriveCapabilityKind,
  parseClientGuidance,
  prettyJson,
  stringifyWorkflowPayload,
  workflowTriggerPayload,
  withSchemaDescription,
} from '../publishSchemaBuilder';
import type {
  DataModelOperation,
  DataModelSource,
  ExternalMcpConnection,
  KnowledgeBaseSource,
  McpCapabilityKind,
  McpClient,
  PublishedCapability,
  PublishSourceKind,
  ToolGroup,
  WorkspaceAssetCatalog,
  WorkspacePublishAsset,
  WorkflowSource,
} from '../governedMcpTypes';
import {
  GatewayDrawer,
  MaximizableTextarea,
  ToggleRow,
  fieldClass,
  ghostBtn,
  labelClass,
  primaryBtn,
} from '../components/GatewayUi';
import IngestionEntityModal, { type IngestionEntityPayload } from '../../configurations/ingestion/popups/IngestionEntityModal';
import { copilotContextService } from '../../configurations/ingestion/popups/copilotContextService';
import AssetExplorerModal from './AssetExplorerModal';
import KnowledgeBasePickerModal from './KnowledgeBasePickerModal';
import ModelAssetPickerModal from './ModelAssetPickerModal';
import WorkflowPickerModal from './WorkflowPickerModal';
import { studioCatalogService } from '../studioCatalogService';
import { studioWorkflowService } from '../studioWorkflowService';
import { getApiErrorMessage } from '../../../../components/api/tenantDefaultsService';
import { emptyWorkspaceAssetCatalog, publishCatalogService, workspaceCatalogToSources } from '../publishCatalogService';
import { loadTableColumns } from '../../data-models/governance/loadTableColumns';

function DataModelCatalogActions({
  isLight,
  refreshing,
  onAdd,
  onRefresh,
}: {
  isLight: boolean;
  refreshing: boolean;
  onAdd: () => void;
  onRefresh: () => void;
}) {
  const idle = isLight
    ? 'text-neutral-400 hover:text-indigo-600 hover:bg-indigo-50'
    : 'text-neutral-500 hover:text-indigo-400 hover:bg-neutral-800';
  return (
    <div className="flex items-center gap-0.5">
      <button
        type="button"
        title="Add data model"
        onClick={onAdd}
        className={`p-1 rounded-md transition-colors ${idle}`}
      >
        <Plus size={14} />
      </button>
      <button
        type="button"
        title="Refresh data models"
        onClick={onRefresh}
        disabled={refreshing}
        className={`p-1 rounded-md transition-colors disabled:opacity-50 disabled:pointer-events-none ${idle}`}
      >
        <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
      </button>
    </div>
  );
}

function ClientGuidanceFields({
  customContext,
  useWhen,
  doNotUseWhen,
  placeholder,
  isLight,
  generating,
  onChange,
  onGenerate,
}: {
  customContext: string;
  useWhen: string;
  doNotUseWhen: string;
  placeholder: string;
  isLight: boolean;
  generating: boolean;
  onChange: (patch: { customContext?: string; useWhen?: string; doNotUseWhen?: string }) => void;
  onGenerate: () => void;
}) {
  return (
    <>
      <MaximizableTextarea
        label="Custom context"
        value={customContext}
        onChange={(next) => onChange({ customContext: next })}
        placeholder={placeholder}
        hint="Shared with MCP clients in the input schema. Tell the model when to use this capability and when not to."
        isLight={isLight}
        rows={5}
        copilot
        copilotBusy={generating}
        onCopilot={onGenerate}
      />
      <div className="grid md:grid-cols-2 gap-4">
        <MaximizableTextarea
          label="Use when"
          value={useWhen}
          onChange={(next) => onChange({ useWhen: next })}
          placeholder="When the client should call this tool."
          isLight={isLight}
          rows={4}
        />
        <MaximizableTextarea
          label="Do not use when"
          value={doNotUseWhen}
          onChange={(next) => onChange({ doNotUseWhen: next })}
          placeholder="When the client should skip this tool."
          isLight={isLight}
          rows={4}
        />
      </div>
    </>
  );
}

function CapabilityIconPicker({
  value,
  onChange,
  isLight,
}: {
  value?: string;
  onChange: (key: string) => void;
  isLight: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selectedKey = resolveCapabilityIconKey(value);
  const selected = CAPABILITY_ICON_OPTIONS.find((option) => option.key === selectedKey) || CAPABILITY_ICON_OPTIONS[0];
  const SelectedIcon = selected.icon;

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <label className={labelClass(isLight)}>Icon</label>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`${fieldClass(isLight)} flex items-center justify-between gap-2 text-left`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="inline-flex items-center gap-2 min-w-0">
          <span
            className={`h-7 w-7 rounded-lg flex items-center justify-center shrink-0 ${
              isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'
            }`}
          >
            <SelectedIcon size={15} />
          </span>
          <span className="truncate">{selected.label}</span>
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}
        />
      </button>
      {open ? (
        <div
          className={`absolute z-30 mt-1.5 w-full max-h-64 overflow-y-auto rounded-xl border shadow-xl ${
            isLight ? 'border-neutral-200 bg-white' : 'border-neutral-700 bg-neutral-900'
          }`}
          role="listbox"
        >
          {CAPABILITY_ICON_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isSelected = option.key === selectedKey;
            return (
              <button
                key={option.key}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(option.key);
                  setOpen(false);
                }}
                className={`w-full px-3 py-2.5 flex items-center justify-between gap-2 text-left transition-colors ${
                  isLight ? 'hover:bg-neutral-50 text-neutral-800' : 'hover:bg-neutral-800 text-neutral-100'
                } ${isSelected ? (isLight ? 'bg-indigo-50/70' : 'bg-indigo-500/10') : ''}`}
              >
                <span className="inline-flex items-center gap-2 text-sm font-medium">
                  <Icon size={15} className={isLight ? 'text-neutral-600' : 'text-neutral-300'} />
                  {option.label}
                </span>
                {isSelected ? <Check size={14} className="text-indigo-500 shrink-0" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function Chip({
  selected,
  label,
  hint,
  onClick,
  disabled,
  isLight,
}: {
  selected: boolean;
  label: string;
  hint?: string;
  onClick: () => void;
  disabled?: boolean;
  isLight: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`group text-left rounded-xl border px-3 py-2.5 transition-all ${
        disabled
          ? isLight
            ? 'border-neutral-200 bg-neutral-50 text-neutral-400 cursor-not-allowed'
            : 'border-neutral-800 bg-neutral-900 text-neutral-600 cursor-not-allowed'
          : selected
            ? isLight
              ? 'border-indigo-500/70 bg-indigo-50/80 shadow-[0_0_0_3px_rgba(99,102,241,0.12)]'
              : 'border-indigo-400/70 bg-indigo-500/15 shadow-[0_0_0_3px_rgba(99,102,241,0.18)]'
            : isLight
              ? 'border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50'
              : 'border-neutral-800 bg-neutral-900 hover:border-neutral-700 hover:bg-neutral-800/60'
      }`}
    >
      <span className="flex items-start gap-2.5">
        <span
          className={`mt-0.5 h-4 w-4 rounded-full border flex items-center justify-center shrink-0 ${
            selected
              ? 'border-indigo-500 bg-indigo-600 text-white'
              : isLight
                ? 'border-neutral-300 bg-white'
                : 'border-neutral-600 bg-neutral-900'
          }`}
        >
          {selected ? <Check size={10} strokeWidth={3} /> : null}
        </span>
        <span className="min-w-0">
          <span className={`block text-sm font-medium leading-5 ${disabled ? '' : isLight ? 'text-neutral-900' : 'text-white'}`}>
            {label}
          </span>
          {hint ? <span className={`block text-[11px] mt-0.5 leading-4 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{hint}</span> : null}
        </span>
      </span>
    </button>
  );
}

function Section({
  step,
  title,
  hint,
  action,
  children,
  isLight,
}: {
  step?: number;
  title: string;
  hint?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  isLight: boolean;
}) {
  return (
    <section className="px-6 py-6">
      <header className="flex items-start justify-between gap-3 mb-5">
        <div className="flex items-start gap-3 min-w-0">
          {step != null ? (
            <span
              className={`mt-0.5 h-6 w-6 rounded-full text-[11px] font-semibold flex items-center justify-center shrink-0 ${
                isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'
              }`}
            >
              {step}
            </span>
          ) : null}
          <div className="min-w-0">
            <h3 className={`text-[15px] font-semibold tracking-tight ${isLight ? 'text-neutral-900' : 'text-white'}`}>{title}</h3>
            {hint ? <p className={`text-sm mt-1 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{hint}</p> : null}
          </div>
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function CompactSwitch({
  checked,
  onChange,
  isLight,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  isLight: boolean;
  label: string;
}) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} className="inline-flex items-center gap-2.5">
      <span
        className={`relative w-9 h-5 rounded-full shrink-0 transition-colors ${
          checked ? 'bg-indigo-600' : isLight ? 'bg-neutral-300' : 'bg-neutral-700'
        }`}
      >
        <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </span>
      <span className={`text-sm font-medium ${checked ? (isLight ? 'text-neutral-800' : 'text-neutral-200') : isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
        {label}
      </span>
    </button>
  );
}

function ChoiceCard({
  selected,
  disabled,
  isLight,
  icon: Icon,
  label,
  hint,
  onClick,
  trailing,
  role,
}: {
  selected: boolean;
  disabled?: boolean;
  isLight: boolean;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  hint?: string;
  onClick: () => void;
  trailing?: React.ReactNode;
  role?: 'radio';
}) {
  return (
    <div
      className={`relative flex items-start rounded-xl border ${
        disabled
          ? isLight
            ? 'border-neutral-200 bg-neutral-50 text-neutral-400'
            : 'border-neutral-800 bg-neutral-950 text-neutral-600'
          : selected
            ? isLight
              ? 'border-indigo-400 bg-indigo-50/70'
              : 'border-indigo-400/70 bg-indigo-500/15'
            : isLight
              ? 'border-neutral-200 bg-white'
              : 'border-neutral-800 bg-neutral-950'
      }`}
    >
      <button
        type="button"
        role={role}
        aria-checked={role === 'radio' ? selected : undefined}
        disabled={disabled}
        onClick={onClick}
        className={`min-w-0 flex-1 flex items-start gap-3 px-3.5 py-3 text-left transition-colors ${
          trailing ? 'pr-16' : ''
        } ${
          disabled
            ? 'cursor-not-allowed'
            : isLight
              ? 'hover:bg-neutral-50/80'
              : 'hover:bg-white/[0.03]'
        }`}
      >
        <Icon size={16} className={`mt-0.5 shrink-0 ${selected ? 'text-indigo-600' : isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
        <span className="min-w-0 flex-1">
          <span className={`block text-sm font-medium ${disabled ? '' : isLight ? 'text-neutral-900' : 'text-white'}`}>
            {label}
            {disabled ? ' · soon' : ''}
          </span>
          {hint ? <span className={`block text-[11px] mt-0.5 leading-4 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{hint}</span> : null}
        </span>
        <span
          className={`mt-0.5 h-4 w-4 rounded-full border flex items-center justify-center shrink-0 ${
            selected
              ? 'border-indigo-600 bg-indigo-600 text-white'
              : isLight
                ? 'border-neutral-300 bg-white'
                : 'border-neutral-600 bg-neutral-900'
          }`}
        >
          {selected ? <Check size={10} strokeWidth={3} /> : null}
        </span>
      </button>
      {trailing ? <div className="absolute top-2 right-9">{trailing}</div> : null}
    </div>
  );
}

function GuideSidebar({ isLight }: { isLight: boolean }) {
  const steps = [
    ['Define', 'Provide a name, description, and type.'],
    ['Configure', 'Select clients and connect a source.'],
    ['Publish', 'Review and publish for clients to use.'],
  ];
  const tips = [
    'Use clear, descriptive names',
    'Add a concise description',
    'Choose the right capability type',
    'Restrict access to specific clients',
    'Connect to a trusted data source',
  ];
  return (
    <aside className={`h-full min-h-full px-6 py-7 ${isLight ? 'bg-[#F4F3FF]' : 'bg-indigo-500/[0.07]'}`}>
      <div className={`relative h-32 rounded-2xl overflow-hidden mb-6 ${isLight ? 'bg-white' : 'bg-white/5'}`}>
        <span className={`absolute -right-6 -top-8 h-28 w-28 rounded-full ${isLight ? 'bg-indigo-100' : 'bg-indigo-500/20'}`} />
        <span className={`absolute -left-4 bottom-0 h-16 w-16 rounded-full ${isLight ? 'bg-violet-100' : 'bg-violet-500/15'}`} />
        <span className={`absolute inset-0 flex items-center justify-center ${isLight ? 'text-indigo-400' : 'text-indigo-300'}`}>
          <Wrench size={34} />
        </span>
      </div>
      <h4 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>About capabilities</h4>
      <p className={`text-sm mt-2 leading-relaxed ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
        Capabilities expose governed data, tools, and resources to AI clients in a secure and controlled way.
      </p>
      <ol className="mt-6 space-y-3">
        {steps.map(([label, detail], index) => (
          <li key={label} className="flex items-start gap-3">
            <span
              className={`h-5 w-5 rounded-full text-[10px] font-semibold flex items-center justify-center shrink-0 mt-0.5 ${
                isLight ? 'bg-white text-indigo-600 shadow-sm' : 'bg-indigo-500/20 text-indigo-300'
              }`}
            >
              {index + 1}
            </span>
            <span>
              <span className={`block text-sm font-medium ${isLight ? 'text-neutral-900' : 'text-white'}`}>{label}</span>
              <span className={`block text-xs mt-0.5 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{detail}</span>
            </span>
          </li>
        ))}
      </ol>
      <div className={`mt-6 rounded-2xl p-4 ${isLight ? 'bg-white shadow-sm' : 'bg-neutral-900 ring-1 ring-white/10'}`}>
        <p className={`flex items-center gap-2 text-sm font-semibold mb-3 ${isLight ? 'text-neutral-900' : 'text-white'}`}>
          <Lightbulb size={15} className="text-indigo-500" />
          Tips for success
        </p>
        <ul className="space-y-2">
          {tips.map((tip) => (
            <li key={tip} className={`flex items-start gap-2 text-xs leading-5 ${isLight ? 'text-neutral-600' : 'text-neutral-400'}`}>
              <Check size={13} className="mt-0.5 text-indigo-500 shrink-0" />
              {tip}
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

const SOURCE_ICONS: Record<PublishSourceKind, React.ComponentType<{ size?: number; className?: string }>> = {
  data_model: Database,
  workflow: Workflow,
  knowledge_base: BookOpen,
  external_mcp: Plug,
  custom_text: FileText,
};

function toggleList(list: string[], id: string): string[] {
  return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
}

function toggleOp(list: DataModelOperation[], value: DataModelOperation): DataModelOperation[] {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

function MultiSelectDropdown({
  label,
  values,
  options,
  onChange,
  isLight,
  placeholder = 'Select…',
}: {
  label: string;
  values: string[];
  options: { id: string; label: string; hint?: string; locked?: boolean }[];
  onChange: (next: string[]) => void;
  isLight: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [open]);

  const selected = options.filter((option) => values.includes(option.id));
  const filtered = options.filter((option) => {
    const haystack = `${option.label} ${option.hint || ''}`.toLowerCase();
    return haystack.includes(query.trim().toLowerCase());
  });

  return (
    <div ref={rootRef} className="relative">
      <p className={labelClass(isLight)}>{label}</p>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className={`${fieldClass(isLight)} min-h-[46px] h-auto py-1.5 flex items-center justify-between gap-2 text-left`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="flex flex-wrap gap-1.5 min-w-0 flex-1">
          {selected.length ? (
            selected.map((option) => (
              <span
                key={option.id}
                className={`inline-flex items-center gap-1 max-w-full rounded-full pl-2.5 pr-1 py-0.5 text-xs font-medium ${
                  isLight ? 'bg-indigo-50 text-indigo-700' : 'bg-indigo-500/15 text-indigo-200'
                }`}
              >
                <span className="truncate">{option.label}</span>
                {option.locked ? null : (
                  <span
                    role="button"
                    tabIndex={0}
                    className={`rounded-full p-0.5 ${isLight ? 'hover:bg-indigo-100' : 'hover:bg-indigo-500/30'}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onChange(toggleList(values, option.id));
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        onChange(toggleList(values, option.id));
                      }
                    }}
                    aria-label={`Remove ${option.label}`}
                  >
                    <X size={11} />
                  </span>
                )}
              </span>
            ))
          ) : (
            <span className={isLight ? 'text-neutral-400' : 'text-neutral-500'}>{placeholder}</span>
          )}
        </span>
        <ChevronDown size={14} className={`shrink-0 ${open ? 'rotate-180' : ''} ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`} />
      </button>
      {open ? (
        <div
          className={`absolute z-30 mt-1.5 w-full rounded-xl border shadow-xl overflow-hidden ${
            isLight ? 'bg-white border-neutral-200 shadow-neutral-900/10' : 'bg-neutral-900 border-neutral-700 shadow-black/40'
          }`}
        >
          <div className={`flex items-center gap-2 px-3 py-2.5 border-b ${isLight ? 'border-neutral-100' : 'border-neutral-800'}`}>
            <Search size={13} className={isLight ? 'text-neutral-400' : 'text-neutral-500'} />
            <input
              className={`w-full bg-transparent text-sm outline-none ${isLight ? 'text-neutral-900 placeholder:text-neutral-400' : 'text-white placeholder:text-neutral-500'}`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search"
              autoFocus
            />
          </div>
          <ul className="max-h-56 overflow-y-auto py-1" role="listbox">
            {filtered.length ? (
              filtered.map((option) => {
                const checked = values.includes(option.id);
                return (
                  <li key={option.id}>
                    <label
                      className={`flex items-start gap-2.5 px-3 py-2.5 text-sm cursor-pointer ${
                        isLight ? 'hover:bg-neutral-50 text-neutral-800' : 'hover:bg-neutral-800 text-neutral-200'
                      } ${option.locked ? 'opacity-80' : ''}`}
                    >
                      <span
                        className={`mt-0.5 h-4 w-4 rounded-md border flex items-center justify-center shrink-0 ${
                          checked
                            ? 'border-indigo-500 bg-indigo-600 text-white'
                            : isLight
                              ? 'border-neutral-300 bg-white'
                              : 'border-neutral-600 bg-neutral-900'
                        }`}
                      >
                        {checked ? <Check size={10} strokeWidth={3} /> : null}
                      </span>
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        disabled={option.locked && checked}
                        onChange={() => {
                          if (option.locked && checked) return;
                          onChange(toggleList(values, option.id));
                        }}
                      />
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{option.label}</span>
                        {option.hint ? (
                          <span className={`block text-[11px] ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>{option.hint}</span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                );
              })
            ) : (
              <li className={`px-3 py-2.5 text-sm ${isLight ? 'text-neutral-400' : 'text-neutral-500'}`}>No matches</li>
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function WorkflowTriggerDetails({
  replyMode,
  fields,
  samplePayloadJson,
  triggerType,
  isLight,
  muted,
}: {
  replyMode?: string;
  fields?: { key: string; required?: boolean }[];
  samplePayloadJson?: string | null;
  triggerType?: string;
  isLight: boolean;
  muted: string;
}) {
  const payload = prettyJson(JSON.stringify(workflowTriggerPayload(fields, samplePayloadJson))) || '{}';
  const waits = replyMode === 'wait_for_result';
  const trigger = triggerType && triggerType !== 'manual' ? triggerType : 'manual payload';
  return (
    <div className={isLight ? 'rounded-lg border border-neutral-200 bg-white p-3 space-y-2' : 'rounded-lg border border-neutral-800 bg-neutral-950 p-3 space-y-2'}>
      <p className={`text-xs font-semibold ${isLight ? 'text-neutral-800' : 'text-neutral-100'}`}>How to trigger</p>
      <p className={`text-[11px] ${muted}`}>
        Start this {trigger} workflow
        {waits ? ' and wait for the run result.' : '. The tool acknowledges immediately and the run continues in the background.'}
      </p>
      <pre className={`max-h-36 overflow-auto rounded-md px-2.5 py-2 text-[11px] leading-5 ${isLight ? 'bg-neutral-50 text-neutral-700' : 'bg-neutral-900 text-neutral-300'}`}>
        {payload}
      </pre>
    </div>
  );
}

function blankPublished(endpointId: string, groupIds: string[] = [], capability: McpCapabilityKind = 'tool'): PublishedCapability {
  const assigned = groupIds.filter(Boolean);
  return {
    id: createId('pub'),
    capabilityKind: 'information',
    backedBy: 'data_model',
    sourceId: '',
    sourceName: '',
    mcpRepresentations: capability === 'resource' ? ['resource'] : ['tool'],
    mcpToolName: '',
    icon: DEFAULT_CAPABILITY_ICON,
    mcpResourceUri: '',
    mcpTaskName: '',
    description: '',
    inputSchema: '{ }',
    outputSchema: '{ }',
    useWhen: '',
    doNotUseWhen: '',
    usageGuidance: '',
    endpointId,
    allowedClientIds: [],
    allowedUserIds: [],
    allowedGroupIds: [],
    environment: 'development',
    riskLevel: 'medium',
    dataClassification: 'internal',
    rateLimitPerMinute: 20,
    maxRecords: 25,
    requiresApproval: false,
    outputGovernance: true,
    maskingEnabled: true,
    promptInjectionProtection: true,
    piiDetection: true,
    secretsLeakageProtection: true,
    jailbreakProtection: true,
    version: '1.0.0',
    status: 'published',
    readOnly: true,
    destructive: false,
    hasSideEffects: false,
    isIdempotent: true,
    requiresConfirmation: false,
    selectedExistingToolNames: [],
    selectedExistingResourceUris: [],
    groupId: assigned[0] || '',
    groupIds: assigned,
    resourceSchema: '',
    mcpCapability: capability,
    sourceKind: 'data_model',
    dataModelOperations: ['query'],
    selectedTableIds: [],
    selectedKnowledgeBaseIds: [],
    publishScope: 'data_model',
    assetKind: '',
    assetName: '',
    customContext: '',
    customResourceText: '',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function sourceKindFromBackedBy(backedBy: PublishedCapability['backedBy']): PublishSourceKind {
  if (backedBy === 'external_mcp') return 'external_mcp';
  if (backedBy === 'knowledge_base') return 'knowledge_base';
  if (backedBy === 'workflow' || backedBy === 'long_running_workflow') return 'workflow';
  return 'data_model';
}

function mergeGroupIds(primaryId: string, extraIds: string[]): string[] {
  const extras = extraIds.filter((id) => id && id !== primaryId);
  return primaryId ? [primaryId, ...extras] : extras;
}

export default function PublishCapabilityDrawer({
  open,
  initial,
  clients,
  dataModels,
  workspaceCatalog,
  workflows,
  externalMcp,
  knowledgeBases = [],
  toolGroups,
  defaultGroupId,
  catalogLoading,
  catalogError,
  published = [],
  onRetryCatalogs,
  isLight,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: PublishedCapability | null;
  clients: McpClient[];
  dataModels: DataModelSource[];
  workspaceCatalog?: WorkspaceAssetCatalog;
  workflows: WorkflowSource[];
  externalMcp: ExternalMcpConnection[];
  knowledgeBases?: KnowledgeBaseSource[];
  toolGroups: ToolGroup[];
  defaultGroupId?: string;
  defaultCapability?: McpCapabilityKind;
  catalogLoading?: boolean;
  catalogError?: string | null;
  published?: PublishedCapability[];
  onRetryCatalogs?: () => void;
  isLight: boolean;
  onClose: () => void;
  onSave: (item: PublishedCapability) => void;
}) {
  const [form, setForm] = useState<PublishedCapability>(blankPublished(SINGLETON_ENDPOINT_ID, defaultGroupId ? [defaultGroupId] : []));
  const [error, setError] = useState<string | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [catalogModels, setCatalogModels] = useState<DataModelSource[]>(dataModels);
  const [assetCatalog, setAssetCatalog] = useState<WorkspaceAssetCatalog>(workspaceCatalog || emptyWorkspaceAssetCatalog());
  const [refreshingModels, setRefreshingModels] = useState(false);
  const [showIngestionModal, setShowIngestionModal] = useState(false);
  const [showAssetExplorer, setShowAssetExplorer] = useState(false);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [showKnowledgePicker, setShowKnowledgePicker] = useState(false);
  const [showWorkflowPicker, setShowWorkflowPicker] = useState(false);
  const [pickedWorkflow, setPickedWorkflow] = useState<WorkflowSource | null>(null);
  const [workflowContractLoading, setWorkflowContractLoading] = useState(false);
  const [workflowContractError, setWorkflowContractError] = useState<string | null>(null);
  const [pickedKnowledgeBases, setPickedKnowledgeBases] = useState<KnowledgeBaseSource[]>([]);
  const [creatingModel, setCreatingModel] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [generatingContext, setGeneratingContext] = useState(false);
  const lastSchemaKey = useRef('');
  const skipNextSchemaApply = useRef(true);

  useEffect(() => {
    setCatalogModels(dataModels);
  }, [dataModels]);

  useEffect(() => {
    if (workspaceCatalog) setAssetCatalog(workspaceCatalog);
  }, [workspaceCatalog]);

  useEffect(() => {
    if (!open) return;
    const assigned = initial ? capabilityGroupIds(initial) : defaultGroupId ? [defaultGroupId] : [];
    const next = initial
      ? {
          ...blankPublished(SINGLETON_ENDPOINT_ID, assigned),
          ...initial,
          mcpCapability:
            initial.mcpCapability ||
            (initial.mcpRepresentations?.includes('resource') && !initial.mcpRepresentations?.includes('tool') ? 'resource' : 'tool'),
          sourceKind: initial.sourceKind || sourceKindFromBackedBy(initial.backedBy),
          dataModelOperations: initial.dataModelOperations || [],
          selectedTableIds: initial.selectedTableIds || [],
          selectedKnowledgeBaseIds:
            initial.selectedKnowledgeBaseIds?.length
              ? initial.selectedKnowledgeBaseIds
              : (initial.sourceKind || sourceKindFromBackedBy(initial.backedBy)) === 'knowledge_base' && initial.sourceId
                ? [initial.sourceId]
                : [],
          publishScope: initial.publishScope || (isLegacyAssetPublishedTool(initial) ? 'asset' : 'data_model'),
          assetKind: initial.assetKind || '',
          assetName: initial.assetName || '',
          mcpToolName: splitAssetToolName(initial.mcpToolName || '').slug,
          customContext: initial.customContext || '',
          customResourceText: initial.customResourceText || '',
          useWhen: initial.useWhen || '',
          doNotUseWhen: initial.doNotUseWhen || '',
          groupIds: assigned,
          groupId: assigned[0] || '',
          endpointId: SINGLETON_ENDPOINT_ID,
          status: initial.status === 'disabled' ? ('disabled' as const) : ('published' as const),
        }
      : blankPublished(SINGLETON_ENDPOINT_ID, assigned, 'tool');
    setForm({ ...next, icon: resolveCapabilityIconKey(next.icon) });
    setError(null);
    setAdvancedOpen(false);
    setShowIngestionModal(false);
    setShowAssetExplorer(false);
    setShowAssetPicker(false);
    setShowKnowledgePicker(false);
    setShowWorkflowPicker(false);
    setPickedWorkflow(null);
    setWorkflowContractError(null);
    setPickedKnowledgeBases([]);
    setGeneratingContext(false);
    setCreateError(null);
    lastSchemaKey.current = '';
    skipNextSchemaApply.current = true;
    logGovernedMcp('app', 'publish_popup_open', { edit: Boolean(initial), groupIds: next.groupIds, capability: next.mcpCapability });
  }, [open, initial, defaultGroupId]);

  const selectedWorkflow = pickedWorkflow?.id === form.sourceId
    ? pickedWorkflow
    : workflows.find((item) => item.id === form.sourceId);
  const selectedExternal = externalMcp.find((item) => item.id === form.sourceId);
  const selectedAsset =
    assetCatalog.assets.find((item) => form.selectedTableIds.includes(item.id)) ||
    assetCatalog.assets.find((item) => item.id === splitAssetToolName(initial?.mcpToolName || '').assetId) ||
    null;
  const isLegacyAssetEditor = isLegacyAssetPublishedTool(initial) && form.publishScope !== 'data_model';
  const isModelScope = form.sourceKind === 'data_model' && !isLegacyAssetEditor;
  const modelAssets = assetCatalog.assets.filter(
    (item) => item.modelId === form.sourceId && isQueryablePublishAsset(item),
  );
  const activeModel =
    catalogModels.find((item) => item.id === form.sourceId) ||
    catalogModels.find((item) => item.id === selectedAsset?.modelId) ||
    null;
  const sourceOptions = form.mcpCapability === 'resource' ? RESOURCE_SOURCE_OPTIONS : TOOL_SOURCE_OPTIONS;
  const toolSlug = splitAssetToolName(form.mcpToolName).slug;
  const composedToolName = isModelScope
    ? toolSlug
    : composeAssetToolName(selectedAsset?.id || form.selectedTableIds[0] || '', toolSlug);
  const isActive = form.status !== 'disabled';
  const primaryGroupId = form.groupId || capabilityGroupIds(form)[0] || '';
  const knowledgeCatalog = useMemo(() => {
    const map = new Map<string, KnowledgeBaseSource>();
    knowledgeBases.forEach((item) => map.set(item.id, item));
    pickedKnowledgeBases.forEach((item) => map.set(item.id, item));
    return map;
  }, [knowledgeBases, pickedKnowledgeBases]);
  const selectedKnowledgeBaseIds =
    form.selectedKnowledgeBaseIds?.length
      ? form.selectedKnowledgeBaseIds
      : form.sourceKind === 'knowledge_base' && form.sourceId
        ? [form.sourceId]
        : [];
  const selectedKnowledgeBases = selectedKnowledgeBaseIds
    .map((id) => knowledgeCatalog.get(id) || { id, name: id, description: '' })
    .filter((item) => item.id);

  const applySchemas = (next: PublishedCapability): PublishedCapability => {
    const model = catalogModels.find((item) => item.id === next.sourceId) || activeModel;
    const modelTables = model?.tables || [];
    const chosenTables = modelTables.filter((table) => next.selectedTableIds.includes(table.id));
    const workflow = workflows.find((item) => item.id === next.sourceId);
    const kbIds = (next.selectedKnowledgeBaseIds?.length ? next.selectedKnowledgeBaseIds : next.sourceId ? [next.sourceId] : []).filter(Boolean);
    const kb = knowledgeCatalog.get(kbIds[0] || '') || knowledgeBases.find((item) => item.id === next.sourceId);
    const longRunning = Boolean(workflow?.longRunning) || next.workflowReplyMode === 'wait_for_result';
    const guidance = composeClientGuidance(next.customContext, next.useWhen, next.doNotUseWhen);
    const capabilityKind = deriveCapabilityKind({
      mcpCapability: next.mcpCapability,
      sourceKind: next.sourceKind,
      operations: next.dataModelOperations,
      longRunning,
    });
    const representations: PublishedCapability['mcpRepresentations'] =
      next.mcpCapability === 'resource' ? ['resource'] : capabilityKind === 'process' ? ['tool', 'task'] : ['tool'];
    let patched: PublishedCapability = {
      ...next,
      selectedKnowledgeBaseIds: next.sourceKind === 'knowledge_base' ? kbIds : next.selectedKnowledgeBaseIds || [],
      capabilityKind,
      mcpRepresentations: representations,
      backedBy:
        next.sourceKind === 'external_mcp'
          ? 'external_mcp'
          : next.sourceKind === 'knowledge_base'
            ? 'knowledge_base'
            : next.sourceKind === 'workflow'
              ? longRunning
                ? 'long_running_workflow'
                : 'workflow'
              : 'data_model',
      readOnly: !next.dataModelOperations.some((op) => op === 'create' || op === 'update' || op === 'delete'),
      destructive: next.dataModelOperations.includes('delete'),
      hasSideEffects:
        next.dataModelOperations.some((op) => op === 'create' || op === 'update' || op === 'delete') || next.sourceKind === 'workflow',
    };

    if (next.sourceKind === 'data_model' && model) {
      const schemas = buildDataModelSchemas({
        model,
        tables: chosenTables.length ? chosenTables : modelTables,
        operations: next.dataModelOperations,
        capability: next.mcpCapability,
        publishScope: next.publishScope,
        customContext: guidance,
      });
      patched = {
        ...patched,
        inputSchema: schemas.inputSchema,
        outputSchema: schemas.outputSchema,
        resourceSchema: schemas.resourceSchema,
        mcpResourceUri: `selfic://models/${model.code}/${(chosenTables[0] || modelTables[0])?.id || 'records'}`,
      };
    } else if (next.sourceKind === 'workflow') {
      const contract =
        next.workflowInputFields || next.workflowReplyMode || next.workflowGraphSource
          ? { fields: next.workflowInputFields || [], samplePayloadJson: next.workflowSamplePayloadJson || null }
          : null;
      const schemas = buildWorkflowSchemas(longRunning, guidance, contract);
      patched = {
        ...patched,
        inputSchema: schemas.inputSchema,
        outputSchema: schemas.outputSchema,
        mcpTaskName: longRunning ? `${workflow?.code || 'task'}_task` : '',
        requiresConfirmation: Boolean((next.workflowInputFields || []).some((field) => field.required)),
      };
    } else if (next.sourceKind === 'knowledge_base' && (kb || next.sourceName || kbIds.length)) {
      const schemas = buildKnowledgeBaseSchemas(kb?.name || next.sourceName || 'knowledge_base', guidance);
      patched = { ...patched, inputSchema: schemas.inputSchema, outputSchema: schemas.outputSchema };
    } else if (next.sourceKind === 'custom_text') {
      patched = {
        ...patched,
        resourceSchema: next.customResourceText,
        mcpResourceUri: next.mcpResourceUri || `selfic://custom/${next.mcpToolName || 'resource'}`,
      };
    }
    if (guidance && next.sourceKind !== 'custom_text' && next.sourceKind !== 'workflow') {
      patched = { ...patched, inputSchema: withSchemaDescription(patched.inputSchema, guidance) };
    }
    return patched;
  };

  const schemaSourceKey = useMemo(
    () =>
      [
        form.mcpCapability,
        form.sourceKind,
        form.sourceId,
        form.selectedTableIds.join(','),
        selectedKnowledgeBaseIds.join(','),
        form.dataModelOperations.join(','),
        form.publishScope || '',
        form.customContext || '',
        form.useWhen || '',
        form.doNotUseWhen || '',
        form.customResourceText,
        form.selectedExistingToolNames.join(','),
        selectedAsset?.id || '',
        (form.workflowInputFields || []).map((field) => `${field.key}:${field.required ? '1' : '0'}:${field.dataType || ''}`).join(','),
        form.workflowSamplePayloadJson || '',
      ].join('|'),
    [
      form.mcpCapability,
      form.sourceKind,
      form.sourceId,
      form.selectedTableIds,
      selectedKnowledgeBaseIds,
      form.dataModelOperations,
      form.publishScope,
      form.customContext,
      form.useWhen,
      form.doNotUseWhen,
      form.customResourceText,
      form.selectedExistingToolNames,
      selectedAsset?.id,
      form.workflowInputFields,
      form.workflowSamplePayloadJson,
    ]
  );

  useEffect(() => {
    if (!open) return;
    if (skipNextSchemaApply.current) {
      skipNextSchemaApply.current = false;
      lastSchemaKey.current = schemaSourceKey;
      return;
    }
    if (lastSchemaKey.current === schemaSourceKey) return;
    lastSchemaKey.current = schemaSourceKey;
    setForm((prev) => applySchemas(prev));
    logGovernedMcp('app', 'publish_schemas_applied', {
      sourceKind: form.sourceKind,
      tables: form.selectedTableIds,
      operations: form.dataModelOperations,
    });
  }, [
    open,
    schemaSourceKey,
    form.sourceKind,
    form.sourceId,
    form.selectedTableIds,
    form.dataModelOperations,
  ]);

  const applySourceKind = (sourceKind: PublishSourceKind) => {
    setPickedWorkflow(null);
    setWorkflowContractError(null);
    setShowWorkflowPicker(sourceKind === 'workflow');
    setForm((prev) => ({
      ...prev,
      sourceKind,
      sourceId: '',
      sourceName: '',
      selectedTableIds: [],
      selectedKnowledgeBaseIds: [],
      dataModelOperations: sourceKind === 'data_model' ? ['query'] : [],
      publishScope: sourceKind === 'data_model' ? 'data_model' : undefined,
      assetKind: '',
      assetName: '',
      selectedExistingToolNames: [],
      selectedExistingResourceUris: [],
      customResourceText: sourceKind === 'custom_text' ? prev.customResourceText : '',
      workflowGraphSource: undefined,
      workflowReplyMode: undefined,
      workflowInputFields: undefined,
      workflowSamplePayloadJson: undefined,
    }));
    setError(null);
    logGovernedMcp('app', 'publish_source_kind_selected', { sourceKind });
  };

  const applyKnowledgeBases = (ids: string[], items: KnowledgeBaseSource[]) => {
    setPickedKnowledgeBases((prev) => {
      const map = new Map(prev.map((item) => [item.id, item]));
      items.forEach((item) => map.set(item.id, item));
      return Array.from(map.values());
    });
    const names = ids.map((id) => items.find((item) => item.id === id)?.name || knowledgeCatalog.get(id)?.name || id);
    setForm((prev) => ({
      ...prev,
      selectedKnowledgeBaseIds: ids,
      sourceId: ids[0] || '',
      sourceName: names.join(', '),
    }));
    setShowKnowledgePicker(false);
    setError(null);
    logGovernedMcp('app', 'publish_knowledge_bases_selected', { count: ids.length, sourceId: ids[0] || '' });
  };

  const applyListedSource = (sourceId: string, sourceName: string, catalog?: WorkspaceAssetCatalog) => {
    const queryable = (catalog || assetCatalog).assets.filter(
      (item) => item.modelId === sourceId && isQueryablePublishAsset(item),
    );
    setForm((prev) => ({
      ...prev,
      sourceId,
      sourceName,
      selectedTableIds: prev.sourceKind === 'data_model' && prev.publishScope !== 'asset' ? queryable.map((item) => item.id) : [],
      dataModelOperations:
        prev.sourceKind === 'data_model'
          ? prev.dataModelOperations.length
            ? prev.dataModelOperations
            : ['query']
          : [],
      publishScope: prev.sourceKind === 'data_model' ? 'data_model' : prev.publishScope,
      assetKind: prev.sourceKind === 'data_model' ? '' : prev.assetKind,
      assetName: prev.sourceKind === 'data_model' ? '' : prev.assetName,
    }));
    setError(null);
    logGovernedMcp('app', 'publish_source_selected', { sourceId, sourceKind: form.sourceKind });
  };

  const applyWorkflow = async (workflow: WorkflowSource) => {
    applyListedSource(workflow.id, workflow.name);
    setPickedWorkflow(workflow);
    setShowWorkflowPicker(false);
    const fields = workflow.inputFields || [];
    setWorkflowContractError(null);
    setForm((prev) => ({
      ...prev,
      sourceId: workflow.id,
      sourceName: workflow.name,
      workflowGraphSource: workflow.graphSource || 'draft',
      workflowReplyMode: workflow.replyMode || 'acknowledge',
      workflowInputFields: fields,
      workflowSamplePayloadJson: stringifyWorkflowPayload(workflowTriggerPayload(fields, workflow.samplePayloadJson)),
      requiresConfirmation: fields.some((field) => field.required),
    }));
    logGovernedMcp('app', 'publish_workflow_selected', {
      sourceId: workflow.id,
      fields: fields.length,
      replyMode: workflow.replyMode || 'acknowledge',
      graphSource: workflow.graphSource || 'draft',
    });
    setWorkflowContractLoading(true);
    try {
      const [contractSettled, detailSettled] = await Promise.allSettled([
        studioWorkflowService.getStartContract(workflow.id),
        studioCatalogService.getById(workflow.id),
      ]);
      const contract = contractSettled.status === 'fulfilled' ? contractSettled.value : null;
      const detail = detailSettled.status === 'fulfilled' ? detailSettled.value : null;
      const contractFields = (contract?.start.fields || [])
        .filter((field) => field.key)
        .map((field) => ({
          key: field.key,
          label: field.label,
          dataType: field.dataType,
          required: field.required,
          helpText: field.helpText,
          options: field.options,
        }));
      const detailFields = detail?.inputFields || [];
      const listedFields = workflow.inputFields || [];
      const nextFields = contractFields.length ? contractFields : detailFields.length ? detailFields : listedFields;
      const sample = contractFields.length
        ? contract?.start.samplePayloadJson
        : detail?.samplePayloadJson || workflow.samplePayloadJson;
      const source = contractFields.length ? 'contract' : detailFields.length ? 'detail' : 'catalog';
      setForm((prev) => ({
        ...prev,
        sourceId: workflow.id,
        sourceName: workflow.name,
        workflowGraphSource: contract?.graphSource || detail?.graphSource || workflow.graphSource || 'draft',
        workflowReplyMode: contract?.replyMode || detail?.replyMode || workflow.replyMode || 'acknowledge',
        workflowInputFields: nextFields,
        workflowSamplePayloadJson: stringifyWorkflowPayload(
          workflowTriggerPayload(nextFields, sample, contract?.runHints?.payloadKeys),
        ),
        requiresConfirmation: nextFields.some((field) => field.required),
      }));
      setWorkflowContractError(null);
      logGovernedMcp('app', 'publish_workflow_contract_ok', {
        sourceId: workflow.id,
        source,
        fields: nextFields.length,
        keys: nextFields.map((field) => field.key),
        replyMode: contract?.replyMode || detail?.replyMode || workflow.replyMode,
        graphSource: contract?.graphSource || detail?.graphSource || workflow.graphSource,
      });
      if (!nextFields.length && contractSettled.status === 'rejected') {
        const message = getApiErrorMessage(contractSettled.reason, 'Unable to load the workflow start contract');
        setWorkflowContractError(message);
        logGovernedMcp('warning', 'publish_workflow_contract_failed', { sourceId: workflow.id, message });
      }
    } finally {
      setWorkflowContractLoading(false);
    }
  };

  const applySelectedModel = (modelId: string) => {
    const model = catalogModels.find((item) => item.id === modelId);
    const queryable = assetCatalog.assets.filter((item) => item.modelId === modelId && isQueryablePublishAsset(item));
    setForm((prev) => ({
      ...prev,
      sourceId: modelId,
      sourceName: model?.name || '',
      selectedTableIds: queryable.map((item) => item.id),
      dataModelOperations: prev.dataModelOperations.length ? prev.dataModelOperations : ['query'],
      publishScope: 'data_model',
      assetKind: '',
      assetName: '',
    }));
    setError(null);
    logGovernedMcp('app', 'publish_model_selected', { sourceId: modelId, assets: queryable.length });
  };

  const toggleSelectedAsset = (assetId: string) => {
    setForm((prev) => {
      const selected = prev.selectedTableIds.includes(assetId)
        ? prev.selectedTableIds.filter((id) => id !== assetId)
        : [...prev.selectedTableIds, assetId];
      return { ...prev, selectedTableIds: selected, publishScope: 'data_model', assetKind: '', assetName: '' };
    });
    setError(null);
  };

  const generateCustomContext = async () => {
    if (generatingContext) return;
    const canGenerate =
      (form.sourceKind === 'data_model' && form.selectedTableIds.length > 0) ||
      (form.sourceKind === 'workflow' && Boolean(form.sourceId)) ||
      (form.sourceKind === 'knowledge_base' && selectedKnowledgeBaseIds.length > 0) ||
      (form.sourceKind === 'external_mcp' && Boolean(form.sourceId));
    if (!canGenerate) return;

    const model = catalogModels.find((item) => item.id === form.sourceId) || activeModel;
    setGeneratingContext(true);
    setError(null);
    logGovernedMcp('app', 'publish_custom_context_generate', {
      sourceKind: form.sourceKind,
      sourceId: form.sourceId,
      assets: form.selectedTableIds.length,
      knowledgeBases: selectedKnowledgeBaseIds.length,
    });
    try {
      let metadataStructure = '';
      let modelName = form.sourceName || form.mcpToolName || 'capability';
      if (form.sourceKind === 'data_model') {
        const selectedAssets = assetCatalog.assets.filter((item) => form.selectedTableIds.includes(item.id));
        const selectedTables = (model?.tables || []).filter((table) => form.selectedTableIds.includes(table.id));
        if (!selectedAssets.length) {
          throw new Error('Selected tables are not available in the workspace catalog.');
        }
        const described = await Promise.all(
          selectedAssets.map(async (asset) => {
            if (!asset.sourceId) return { id: asset.id, columns: [] as { name: string; type: string }[] };
            try {
              const source = assetCatalog.sources.find((item) => item.id === asset.sourceId);
              const columns = await loadTableColumns(asset.sourceId, asset.name, asset.path, source?.connectorKey);
              return { id: asset.id, columns: columns.map((column) => ({ name: column.name, type: column.type })) };
            } catch (err) {
              logGovernedMcp('warning', 'publish_custom_context_columns_failed', {
                assetId: asset.id,
                message: err instanceof Error ? err.message : String(err),
              });
              return { id: asset.id, columns: [] as { name: string; type: string }[] };
            }
          }),
        );
        const columnsByAsset = new Map(described.map((item) => [item.id, item.columns]));
        const fieldCount = described.reduce((sum, item) => sum + item.columns.length, 0);
        logGovernedMcp('app', 'publish_custom_context_columns_ok', { assets: described.length, fieldCount });
        if (!fieldCount) {
          throw new Error('Could not read columns from Trino for the selected tables.');
        }
        const entities = (selectedTables.length
          ? selectedTables
          : selectedAssets.map((asset) => ({
              id: asset.id,
              name: asset.name,
              kind: asset.assetKind,
              fields: [],
            }))
        ).map((item) => {
          const live = columnsByAsset.get(item.id) || [];
          const fields = live.length
            ? live.map((column) => ({
                name: column.name,
                display_name: column.name,
                data_type: column.type,
              }))
            : ('fields' in item ? item.fields : []).map((field) => ({
                name: field.name,
                display_name: field.displayName || field.name,
                data_type: field.dataType,
              }));
          return {
            id: item.id,
            display_name: item.name,
            entity_type: ('kind' in item ? item.kind : '') || 'table',
            fields,
          };
        });
        modelName = model?.name || form.sourceName;
        metadataStructure = JSON.stringify({
          catalog: [{ display_name: modelName, entities }],
        });
      } else if (form.sourceKind === 'workflow') {
        metadataStructure = JSON.stringify({
          catalog: [
            {
              display_name: selectedWorkflow?.name || form.sourceName,
              entities: [
                {
                  id: form.sourceId,
                  display_name: selectedWorkflow?.name || form.sourceName,
                  entity_type: selectedWorkflow?.longRunning ? 'long_running_workflow' : 'workflow',
                  description: selectedWorkflow?.description || '',
                  fields: [],
                },
              ],
            },
          ],
        });
      } else if (form.sourceKind === 'knowledge_base') {
        metadataStructure = JSON.stringify({
          catalog: [
            {
              display_name: form.sourceName || 'Knowledge bases',
              entities: selectedKnowledgeBases.map((item) => ({
                id: item.id,
                display_name: item.name,
                entity_type: 'knowledge_base',
                description: item.description || '',
                document_count: item.documentCount,
                fields: [],
              })),
            },
          ],
        });
      } else {
        const tools = (selectedExternal?.tools || []).filter((tool) => form.selectedExistingToolNames.includes(tool.name));
        metadataStructure = JSON.stringify({
          catalog: [
            {
              display_name: selectedExternal?.name || form.sourceName,
              entities: [
                {
                  id: form.sourceId,
                  display_name: selectedExternal?.name || form.sourceName,
                  entity_type: 'external_mcp',
                  description: selectedExternal?.description || '',
                  fields: tools.map((tool) => ({
                    name: tool.name,
                    display_name: tool.name,
                    data_type: 'tool',
                    description: tool.description || '',
                  })),
                },
              ],
            },
          ],
        });
      }

      const analysis = await copilotContextService.analyzeContext({
        metadataStructure,
        modelName,
        followUp: CLIENT_GUIDANCE_FOLLOW_UP,
        priorAnalysis: composeClientGuidance(form.customContext, form.useWhen, form.doNotUseWhen) || undefined,
      });
      const narrative = (analysis.contextNarrative || '').trim();
      if (!narrative) throw new Error('Copilot did not return context for this capability.');
      const parsed = parseClientGuidance(narrative);
      setForm((prev) =>
        parsed.labeled
          ? {
              ...prev,
              customContext: parsed.customContext,
              useWhen: parsed.useWhen,
              doNotUseWhen: parsed.doNotUseWhen,
            }
          : { ...prev, customContext: parsed.customContext },
      );
      logGovernedMcp('app', 'publish_custom_context_generate_ok', {
        sourceKind: form.sourceKind,
        labeled: parsed.labeled,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to generate custom context.';
      setError(message);
      logGovernedMcp('error', 'publish_custom_context_generate_failed', { message });
    } finally {
      setGeneratingContext(false);
    }
  };

  const applySelectedAsset = (asset: WorkspacePublishAsset) => {
    setForm((prev) => ({
      ...prev,
      sourceId: asset.modelId,
      sourceName: `${asset.modelName} → ${asset.name}`,
      selectedTableIds: [asset.id],
      assetKind: asset.assetKind,
      assetName: asset.name,
      dataModelOperations: prev.dataModelOperations.length ? prev.dataModelOperations : ['query'],
    }));
    setShowAssetExplorer(false);
    setError(null);
  };

  const refreshDataModels = useCallback(async (): Promise<{ rows: DataModelSource[]; catalog: WorkspaceAssetCatalog }> => {
    setRefreshingModels(true);
    logGovernedMcp('app', 'publish_data_models_refresh');
    onRetryCatalogs?.();
    try {
      const catalog = await publishCatalogService.listWorkspaceAssets();
      const rows = workspaceCatalogToSources(catalog);
      setAssetCatalog(catalog);
      setCatalogModels(rows);
      logGovernedMcp('app', 'publish_workspace_assets_refresh_ok', { count: catalog.assets.length });
      return { rows, catalog };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to refresh workspace assets';
      logGovernedMcp('error', 'publish_workspace_assets_refresh_failed', { message });
      return { rows: [], catalog: emptyWorkspaceAssetCatalog() };
    } finally {
      setRefreshingModels(false);
    }
  }, [onRetryCatalogs]);

  const handleOpenIngestionModal = () => {
    if (form.sourceKind !== 'data_model') applySourceKind('data_model');
    setCreateError(null);
    setShowIngestionModal(true);
    logGovernedMcp('app', 'publish_add_data_model_opened');
  };

  const handleCreateDataModel = async (payload: IngestionEntityPayload) => {
    setCreatingModel(true);
    setCreateError(null);
    try {
      const created = await publishCatalogService.createDataModel(payload);
      setShowIngestionModal(false);
      const { rows, catalog } = await refreshDataModels();
      const selected = created.id
        ? rows.find((item) => item.id === created.id)
        : rows.find((item) => item.name === created.name);
      const sourceId = selected?.id || created.id;
      const sourceName = selected?.name || created.name;
      if (sourceId) {
        applyListedSource(sourceId, sourceName, catalog);
      }
      logGovernedMcp('app', 'publish_add_data_model_saved', { sourceId, sourceName });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create data model.';
      setCreateError(message);
    } finally {
      setCreatingModel(false);
    }
  };

  const setPrimaryGroup = (groupId: string) => {
    setForm((prev) => {
      const groupIds = mergeGroupIds(groupId, capabilityGroupIds(prev));
      logGovernedMcp('app', 'publish_identity_group_changed', { groupId, groupIds });
      return { ...prev, groupId, groupIds };
    });
  };

  const collectErrors = (): string[] => {
    const issues: string[] = [];
    if (!normalizeMcpToolName(form.mcpToolName.trim()).replace(/_+/g, '')) issues.push('MCP tool name is required.');
    if (!form.description.trim()) issues.push('Description is required.');
    if (!capabilityGroupIds(form).length) issues.push('Assign the capability to a group.');
    if (form.mcpCapability === 'prompt') issues.push('Prompt capabilities are not available yet.');
    if (form.sourceKind === 'custom_text') {
      if (!form.customResourceText.trim()) issues.push('Custom resource text is required.');
    } else if (form.sourceKind === 'knowledge_base') {
      if (!selectedKnowledgeBaseIds.length) issues.push('Select at least one knowledge base.');
    } else if (!form.sourceId) {
      issues.push('Select a source for this capability.');
    }
    if (form.sourceKind === 'data_model') {
      if (!form.dataModelOperations.length) issues.push('Choose at least one data model operation.');
      if (!form.selectedTableIds.length) {
        issues.push(isModelScope ? 'Select at least one asset for this tool.' : 'Select one asset for this tool.');
      }
    }
    if (publishedToolNameTaken(isModelScope ? toolSlug : composedToolName, published, form.id)) {
      issues.push('A published tool with this name already exists.');
    }
    if (form.mcpCapability === 'tool') {
      if (!prettyJson(form.inputSchema) && form.inputSchema.replace(/\s+/g, '') !== '{}') issues.push('Input schema must be valid JSON.');
      if (!prettyJson(form.outputSchema) && form.outputSchema.replace(/\s+/g, '') !== '{}') issues.push('Output schema must be valid JSON.');
    }
    return issues;
  };

  const submit = () => {
    const issues = collectErrors();
    if (issues.length) {
      setError(issues[0]);
      logGovernedMcp('warning', 'publish_submit_blocked', { issues });
      return;
    }
    const assetId = selectedAsset?.id || form.selectedTableIds[0] || '';
    const binding = isModelScope
      ? buildPublishedModelBinding(toolSlug, form.selectedTableIds)
      : buildPublishedAssetBinding(toolSlug, assetId, {
          assetKind: selectedAsset?.assetKind || form.assetKind || '',
          assetName: selectedAsset?.name || form.assetName || '',
        });
    const next = applySchemas({
      ...form,
      ...binding,
      version: form.version.trim() || '1.0.0',
      status: form.status === 'disabled' ? 'disabled' : 'published',
    });
    const groupIds = mergeGroupIds(next.groupId || capabilityGroupIds(next)[0] || '', capabilityGroupIds(next));
    logGovernedMcp('app', 'publish_submit', {
      capability: next.mcpCapability,
      sourceKind: next.sourceKind,
      sourceId: next.sourceId,
      knowledgeBases: next.selectedKnowledgeBaseIds?.length || 0,
      groupIds,
      status: next.status,
    });
    onSave({ ...next, groupIds, groupId: groupIds[0] || '' });
  };

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const title = isLight ? 'text-neutral-900' : 'text-white';
  const sourceLabel = sourceOptions.find((option) => option.value === form.sourceKind)?.label || 'Source';
  const well = isLight
    ? 'rounded-xl border border-neutral-200 bg-neutral-50/80 p-4 space-y-4'
    : 'rounded-xl border border-neutral-800 bg-neutral-950/50 p-4 space-y-4';
  const sheet = isLight ? 'divide-y divide-neutral-100' : 'divide-y divide-neutral-800';

  return (
    <>
    <GatewayDrawer
      open={open}
      icon={<FileText size={18} className="text-indigo-500" />}
      title={initial ? form.mcpToolName || 'Edit capability' : 'Create new capability'}
      subtitle="Name the contract, bind a governed source, then publish. Clients request & Selfic executes."
      isLight={isLight}
      onClose={onClose}
      size="xl"
      widthClass="w-[90%] h-[90%] max-w-none"
      flush
      footer={
        <>
          <span className={`mr-auto hidden sm:flex items-center gap-2 text-xs ${muted}`}>
            <span className={`capitalize ${title}`}>{form.mcpCapability}</span>
            <span className={isLight ? 'text-neutral-300' : 'text-neutral-700'}>·</span>
            <span>{sourceLabel}</span>
            <span className={isLight ? 'text-neutral-300' : 'text-neutral-700'}>·</span>
            <span>
              {form.allowedClientIds.length
                ? `${form.allowedClientIds.length} client${form.allowedClientIds.length === 1 ? '' : 's'}`
                : 'No clients'}
            </span>
          </span>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={primaryBtn()} onClick={submit}>
            {initial ? 'Save' : 'Publish'}
          </button>
        </>
      }
    >
      <div className="h-full grid lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className={`min-h-0 overflow-y-auto ${sheet}`}>
        {error ? (
          <div className="px-6 pt-5">
            <div className="flex items-start gap-2.5 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-500">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              {error}
            </div>
          </div>
        ) : null}

        <Section
          step={1}
          title="Basic information"
          hint="Define what this capability is and how it appears to MCP clients."
          isLight={isLight}
          action={
            <CompactSwitch
              checked={isActive}
              label={isActive ? 'Active' : 'Inactive'}
              isLight={isLight}
              onChange={(next) => {
                const status = next ? 'published' : 'disabled';
                setForm((p) => ({ ...p, status }));
                logGovernedMcp('app', 'publish_active_toggled', { status });
              }}
            />
          }
        >
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className={labelClass(isLight)}>Tool name</label>
              <input
                className={fieldClass(isLight)}
                value={toolSlug}
                onChange={(e) => setForm((p) => ({ ...p, mcpToolName: normalizeMcpToolName(splitAssetToolName(e.target.value).slug) }))}
                placeholder="read_kri_rules"
                onBlur={() => setForm((p) => ({ ...p, mcpToolName: normalizeMcpToolName(splitAssetToolName(p.mcpToolName).slug.trim()) }))}
              />
              <p className={`text-[11px] mt-1 ${muted}`}>
                Spaces become underscores. Claude will call{' '}
                <span className={isLight ? 'text-neutral-700' : 'text-neutral-200'}>
                  {composedToolName || (isModelScope ? 'hr_payroll_query' : 'assetId_tool_name')}
                </span>
                .
              </p>
            </div>
            <CapabilityIconPicker
              isLight={isLight}
              value={form.icon}
              onChange={(icon) => {
                setForm((p) => ({ ...p, icon }));
                logGovernedMcp('app', 'publish_icon_selected', { icon });
              }}
            />
            <div>
              <label className={labelClass(isLight)}>Group</label>
              <select className={fieldClass(isLight)} value={primaryGroupId} onChange={(e) => setPrimaryGroup(e.target.value)}>
                <option value="">Select a group</option>
                {toolGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
              {!toolGroups.length ? (
                <p className={`text-[11px] mt-1 ${muted}`}>Create a group on the Tools tab first.</p>
              ) : null}
            </div>
          </div>
          <div className="mt-4">
            <label className={labelClass(isLight)}>Description</label>
            <textarea
              className={fieldClass(isLight)}
              rows={2}
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="What this capability does, in one or two sentences."
            />
          </div>
        </Section>

        <Section step={2} title="Audience" hint="Which AI clients may call this tool." isLight={isLight}>
          <MultiSelectDropdown
            label="Clients"
            values={form.allowedClientIds}
            options={clients.map((client) => ({
              id: client.id,
              label: client.name,
              hint: client.registrationId,
            }))}
            onChange={(allowedClientIds) => {
              setForm((p) => ({ ...p, allowedClientIds }));
              logGovernedMcp('app', 'publish_clients_changed', { allowedClientIds });
            }}
            isLight={isLight}
            placeholder="None selected"
          />
        </Section>

        <Section step={3} title="Source" hint="What Selfic will execute when a client invokes this capability." isLight={isLight}>
          <p className={labelClass(isLight)}>Backed by</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {sourceOptions.map((option) => {
              const selected = form.sourceKind === option.value;
              const isDataModel = option.value === 'data_model';
              return (
                <ChoiceCard
                  key={option.value}
                  isLight={isLight}
                  selected={selected}
                  icon={SOURCE_ICONS[option.value]}
                  label={option.label}
                  hint={option.hint}
                  onClick={() => applySourceKind(option.value)}
                  trailing={
                    isDataModel && selected ? (
                      <DataModelCatalogActions
                        isLight={isLight}
                        refreshing={refreshingModels || Boolean(catalogLoading)}
                        onAdd={handleOpenIngestionModal}
                        onRefresh={() => {
                          void refreshDataModels();
                        }}
                      />
                    ) : null
                  }
                />
              );
            })}
          </div>

          {catalogLoading ? (
            <p className={`flex items-center gap-2 text-sm mt-4 ${muted}`}>
              <Loader size={14} className="animate-spin" /> Loading catalog…
            </p>
          ) : null}
          {catalogError ? (
            <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-red-500/25 bg-red-500/10 px-3 py-2.5 text-sm text-red-500">
              <span>{catalogError}</span>
              {onRetryCatalogs ? (
                <button type="button" className={ghostBtn(isLight)} onClick={onRetryCatalogs}>
                  Retry
                </button>
              ) : null}
            </div>
          ) : null}

          {form.sourceKind === 'data_model' ? (
            <div className={`${well} mt-4`}>
              {isModelScope ? (
                <>
                  <div>
                    <div className="flex items-center justify-between gap-2">
                      <label className={labelClass(isLight)}>Data model</label>
                      <DataModelCatalogActions
                        isLight={isLight}
                        refreshing={refreshingModels || Boolean(catalogLoading)}
                        onAdd={handleOpenIngestionModal}
                        onRefresh={() => {
                          void refreshDataModels();
                        }}
                      />
                    </div>
                    <select
                      className={fieldClass(isLight)}
                      value={form.sourceId}
                      onChange={(e) => applySelectedModel(e.target.value)}
                    >
                      <option value="">Select a data model</option>
                      {catalogModels.map((model) => (
                        <option key={model.id} value={model.id}>
                          {model.name}
                          {model.description ? ` · ${model.description}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {form.sourceId ? (
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <p className={labelClass(isLight)}>Assets</p>
                        {form.selectedTableIds.length ? (
                          <button
                            type="button"
                            className={`text-[11px] ${isLight ? 'text-indigo-600' : 'text-indigo-300'}`}
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                selectedTableIds: [],
                                publishScope: 'data_model',
                                assetKind: '',
                                assetName: '',
                              }))
                            }
                          >
                            Clear
                          </button>
                        ) : null}
                      </div>
                      {modelAssets.length ? (
                        <>
                          <button
                            type="button"
                            className={`${fieldClass(isLight)} text-left min-h-[46px] h-auto`}
                            onClick={() => setShowAssetPicker(true)}
                          >
                            {form.selectedTableIds.length ? (
                              <span className="block">
                                <span className="block truncate">
                                  {form.selectedTableIds.length} asset{form.selectedTableIds.length === 1 ? '' : 's'} selected
                                </span>
                                <span className={`block text-[11px] truncate ${muted}`}>
                                  {modelAssets
                                    .filter((asset) => form.selectedTableIds.includes(asset.id))
                                    .map((asset) => asset.name)
                                    .join(', ') || 'Choose tables this tool can query'}
                                </span>
                              </span>
                            ) : (
                              <span className={muted}>Filter and choose tables this tool can query</span>
                            )}
                          </button>
                          {form.selectedTableIds.length ? (
                            <div className="flex flex-wrap gap-1.5 mt-2">
                              {modelAssets
                                .filter((asset) => form.selectedTableIds.includes(asset.id))
                                .map((asset) => (
                                  <span
                                    key={asset.id}
                                    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${
                                      isLight
                                        ? 'bg-neutral-100 text-neutral-700'
                                        : 'bg-neutral-800 text-neutral-200'
                                    }`}
                                  >
                                    {asset.name}
                                    <button
                                      type="button"
                                      aria-label={`Remove ${asset.name}`}
                                      className={muted}
                                      onClick={() => toggleSelectedAsset(asset.id)}
                                    >
                                      <X size={11} />
                                    </button>
                                  </span>
                                ))}
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <p className={`text-xs ${muted}`}>This data model has no table or view assets to publish.</p>
                      )}
                    </div>
                  ) : null}
                </>
              ) : (
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <label className={labelClass(isLight)}>Data model → asset</label>
                    <DataModelCatalogActions
                      isLight={isLight}
                      refreshing={refreshingModels || Boolean(catalogLoading)}
                      onAdd={handleOpenIngestionModal}
                      onRefresh={() => {
                        void refreshDataModels();
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    className={`${fieldClass(isLight)} text-left min-h-[46px] h-auto`}
                    onClick={() => setShowAssetExplorer(true)}
                  >
                    {selectedAsset ? (
                      <span className="block">
                        <span className="block truncate">{selectedAsset.modelName} → {selectedAsset.name}</span>
                        <span className={`block text-[11px] ${muted}`}>
                          {selectedAsset.sourceName || 'Source'} · {selectedAsset.assetKind || 'Asset'}
                        </span>
                      </span>
                    ) : (
                      <span className={muted}>Choose source, data model, and asset</span>
                    )}
                  </button>
                </div>
              )}
              {form.sourceId || selectedAsset || activeModel ? (
                <div>
                  <p className={labelClass(isLight)}>Operations</p>
                  <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-2">
                    {DATA_MODEL_OPERATION_OPTIONS.map((option) => (
                      <Chip
                        key={option.value}
                        isLight={isLight}
                        selected={form.dataModelOperations.includes(option.value)}
                        label={option.label}
                        hint={option.hint}
                        onClick={() => setForm((prev) => ({ ...prev, dataModelOperations: toggleOp(prev.dataModelOperations, option.value) }))}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
              {form.sourceKind === 'data_model' && form.selectedTableIds.length ? (
                <ClientGuidanceFields
                  customContext={form.customContext || ''}
                  useWhen={form.useWhen}
                  doNotUseWhen={form.doNotUseWhen}
                  placeholder="Help Claude understand this model, the selected tables, and how to query them."
                  isLight={isLight}
                  generating={generatingContext}
                  onChange={(patch) => setForm((p) => ({ ...p, ...patch }))}
                  onGenerate={() => {
                    void generateCustomContext();
                  }}
                />
              ) : null}
            </div>
          ) : null}

          {form.sourceKind === 'workflow' ? (
            <div className={`${well} mt-4`}>
              <div className="flex items-center justify-between gap-2">
                <label className={labelClass(isLight)}>Workflow</label>
                {form.sourceId ? (
                  <button
                    type="button"
                    className={`text-[11px] ${isLight ? 'text-indigo-600' : 'text-indigo-300'}`}
                    onClick={() => {
                      setPickedWorkflow(null);
                      applyListedSource('', '');
                      setForm((prev) => ({
                        ...prev,
                        workflowGraphSource: undefined,
                        workflowReplyMode: undefined,
                        workflowInputFields: undefined,
                        workflowSamplePayloadJson: undefined,
                        requiresConfirmation: false,
                      }));
                      setWorkflowContractError(null);
                    }}
                  >
                    Clear
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                className={`${fieldClass(isLight)} text-left min-h-[46px] h-auto`}
                onClick={() => setShowWorkflowPicker(true)}
              >
                {selectedWorkflow || form.sourceId ? (
                  <span className="block">
                    <span className="block truncate">{selectedWorkflow?.name || form.sourceName || form.sourceId}</span>
                    <span className={`block text-[11px] truncate ${muted}`}>
                      {selectedWorkflow?.code || form.sourceId}
                      {selectedWorkflow?.projectName ? ` · ${selectedWorkflow.projectName}` : ''}
                    </span>
                  </span>
                ) : (
                  <span className={muted}>Browse Studio projects and pick a workflow</span>
                )}
              </button>
              {selectedWorkflow?.description ? <p className={`text-[11px] ${muted}`}>{selectedWorkflow.description}</p> : null}
              {workflowContractLoading ? (
                <p className={`text-[11px] ${muted}`}>Loading start contract…</p>
              ) : null}
              {workflowContractError ? (
                <p className="text-[11px] text-amber-500">
                  {workflowContractError}. Publishing will use the generic workflow schema.
                </p>
              ) : null}
              {form.sourceId ? (
                <WorkflowTriggerDetails
                  replyMode={form.workflowReplyMode || selectedWorkflow?.replyMode}
                  fields={form.workflowInputFields}
                  samplePayloadJson={form.workflowSamplePayloadJson}
                  triggerType={selectedWorkflow?.triggerType}
                  isLight={isLight}
                  muted={muted}
                />
              ) : null}
              {form.sourceId ? (
                <ClientGuidanceFields
                  customContext={form.customContext || ''}
                  useWhen={form.useWhen}
                  doNotUseWhen={form.doNotUseWhen}
                  placeholder="When to run this workflow, what it does, and what inputs it expects."
                  isLight={isLight}
                  generating={generatingContext}
                  onChange={(patch) => setForm((p) => ({ ...p, ...patch }))}
                  onGenerate={() => {
                    void generateCustomContext();
                  }}
                />
              ) : null}
            </div>
          ) : null}

          {form.sourceKind === 'knowledge_base' ? (
            <div className={`${well} mt-4`}>
              <div className="flex items-center justify-between gap-2">
                <p className={labelClass(isLight)}>Knowledge bases</p>
                {selectedKnowledgeBaseIds.length ? (
                  <button
                    type="button"
                    className={`text-[11px] ${isLight ? 'text-indigo-600' : 'text-indigo-300'}`}
                    onClick={() => applyKnowledgeBases([], [])}
                  >
                    Clear
                  </button>
                ) : null}
              </div>
              <button
                type="button"
                className={`${fieldClass(isLight)} text-left min-h-[46px] h-auto`}
                onClick={() => setShowKnowledgePicker(true)}
              >
                {selectedKnowledgeBaseIds.length ? (
                  <span className="block">
                    <span className="block truncate">
                      {selectedKnowledgeBaseIds.length} knowledge base{selectedKnowledgeBaseIds.length === 1 ? '' : 's'} selected
                    </span>
                    <span className={`block text-[11px] truncate ${muted}`}>
                      {selectedKnowledgeBases.map((item) => item.name).join(', ')}
                    </span>
                  </span>
                ) : (
                  <span className={muted}>Choose a workspace, then select one or more knowledge bases</span>
                )}
              </button>
              {selectedKnowledgeBases.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {selectedKnowledgeBases.map((item) => (
                    <span
                      key={item.id}
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${
                        isLight ? 'bg-neutral-100 text-neutral-700' : 'bg-neutral-800 text-neutral-200'
                      }`}
                    >
                      {item.name}
                      <button
                        type="button"
                        aria-label={`Remove ${item.name}`}
                        className={muted}
                        onClick={() =>
                          applyKnowledgeBases(
                            selectedKnowledgeBaseIds.filter((id) => id !== item.id),
                            selectedKnowledgeBases.filter((kb) => kb.id !== item.id),
                          )
                        }
                      >
                        <X size={11} />
                      </button>
                    </span>
                  ))}
                </div>
              ) : null}
              {selectedKnowledgeBaseIds.length ? (
                <ClientGuidanceFields
                  customContext={form.customContext || ''}
                  useWhen={form.useWhen}
                  doNotUseWhen={form.doNotUseWhen}
                  placeholder="What these knowledge bases contain and how to retrieve from them."
                  isLight={isLight}
                  generating={generatingContext}
                  onChange={(patch) => setForm((p) => ({ ...p, ...patch }))}
                  onGenerate={() => {
                    void generateCustomContext();
                  }}
                />
              ) : null}
            </div>
          ) : null}

          {form.sourceKind === 'external_mcp' ? (
            <div className={`${well} mt-4`}>
              <div>
                <label className={labelClass(isLight)}>External MCP</label>
                <select
                  className={fieldClass(isLight)}
                  value={form.sourceId}
                  onChange={(e) => {
                    const connection = externalMcp.find((item) => item.id === e.target.value);
                    applyListedSource(e.target.value, connection?.name || '');
                  }}
                >
                  <option value="">Select a connected MCP</option>
                  {externalMcp.map((connection) => (
                    <option key={connection.id} value={connection.id}>
                      {connection.name}
                    </option>
                  ))}
                </select>
              </div>
              {selectedExternal ? (
                <div className="grid md:grid-cols-2 gap-2">
                  {selectedExternal.tools.map((tool) => (
                    <Chip
                      key={tool.name}
                      isLight={isLight}
                      selected={form.selectedExistingToolNames.includes(tool.name)}
                      label={tool.name}
                      hint={tool.description}
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          selectedExistingToolNames: [tool.name],
                          inputSchema: prettyJson(tool.inputSchema) || tool.inputSchema,
                        }))
                      }
                    />
                  ))}
                </div>
              ) : null}
              {form.sourceId ? (
                <ClientGuidanceFields
                  customContext={form.customContext || ''}
                  useWhen={form.useWhen}
                  doNotUseWhen={form.doNotUseWhen}
                  placeholder="What this connected MCP provides and how clients should call it."
                  isLight={isLight}
                  generating={generatingContext}
                  onChange={(patch) => setForm((p) => ({ ...p, ...patch }))}
                  onGenerate={() => {
                    void generateCustomContext();
                  }}
                />
              ) : null}
            </div>
          ) : null}

          {form.sourceKind === 'custom_text' ? (
            <div className={`${well} mt-4`}>
              <MaximizableTextarea
                label="Custom resource"
                value={form.customResourceText}
                onChange={(customResourceText) => setForm((p) => ({ ...p, customResourceText }))}
                placeholder="Describe the resource URI, shape, and how agents should read it."
                isLight={isLight}
                rows={8}
                mono
              />
            </div>
          ) : null}
        </Section>

        <div>
          <button
            type="button"
            className={`w-full flex items-center justify-between px-6 py-5 text-left ${isLight ? 'hover:bg-neutral-50' : 'hover:bg-white/[0.03]'}`}
            onClick={() => {
              setAdvancedOpen((value) => !value);
              logGovernedMcp('app', 'publish_advanced_toggled', { open: !advancedOpen });
            }}
            aria-expanded={advancedOpen}
          >
            <span className="flex items-start gap-3 min-w-0">
              <span
                className={`mt-0.5 h-6 w-6 rounded-full text-[11px] font-semibold flex items-center justify-center shrink-0 ${
                  isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-500/15 text-indigo-300'
                }`}
              >
                4
              </span>
              <span>
                <span className={`block text-[15px] font-semibold tracking-tight ${title}`}>Advanced</span>
                <span className={`block text-sm mt-1 ${muted}`}>Schemas, extra groups, and risk controls</span>
              </span>
            </span>
            <span className={`h-8 w-8 rounded-full border flex items-center justify-center shrink-0 ${isLight ? 'border-neutral-200' : 'border-neutral-700'}`}>
              <ChevronDown size={16} className={`transition-transform ${advancedOpen ? 'rotate-180' : ''} ${muted}`} />
            </span>
          </button>
          {advancedOpen ? (
            <div className="px-6 pb-6 space-y-6">
              <div className="pt-5 grid md:grid-cols-[160px_minmax(0,1fr)] gap-4">
                <div>
                  <label className={labelClass(isLight)}>Version</label>
                  <input className={fieldClass(isLight)} value={form.version} onChange={(e) => setForm((p) => ({ ...p, version: e.target.value }))} />
                </div>
                <div>
                  <label className={labelClass(isLight)}>Guidance</label>
                  <textarea
                    className={fieldClass(isLight)}
                    rows={2}
                    value={form.usageGuidance}
                    onChange={(e) => setForm((p) => ({ ...p, usageGuidance: e.target.value }))}
                  />
                </div>
              </div>
              {form.mcpCapability === 'tool' ? (
                <div className="grid md:grid-cols-2 gap-4">
                  <MaximizableTextarea
                    label="Input schema"
                    value={form.inputSchema}
                    onChange={(inputSchema) => setForm((p) => ({ ...p, inputSchema }))}
                    isLight={isLight}
                    rows={10}
                    mono
                    copilot
                    onCopilot={() => {
                      logGovernedMcp('app', 'publish_schema_copilot_clicked', { side: 'input' });
                      setError(null);
                    }}
                  />
                  <MaximizableTextarea
                    label="Output schema"
                    value={form.outputSchema}
                    onChange={(outputSchema) => setForm((p) => ({ ...p, outputSchema }))}
                    isLight={isLight}
                    rows={10}
                    mono
                    copilot
                    onCopilot={() => {
                      logGovernedMcp('app', 'publish_schema_copilot_clicked', { side: 'output' });
                      setError(null);
                    }}
                  />
                </div>
              ) : null}
              <MultiSelectDropdown
                label="Groups"
                values={capabilityGroupIds(form)}
                options={toolGroups.map((group) => ({
                  id: group.id,
                  label: group.name,
                  hint: group.description,
                  locked: group.id === primaryGroupId,
                }))}
                onChange={(nextIds) => {
                  const groupIds = mergeGroupIds(primaryGroupId, nextIds);
                  setForm((p) => ({ ...p, groupIds, groupId: primaryGroupId || groupIds[0] || '' }));
                  logGovernedMcp('app', 'publish_identity_groups_changed', { groupIds });
                }}
                isLight={isLight}
                placeholder="Additional groups"
              />

              <div className={`rounded-xl border p-4 space-y-4 ${isLight ? 'border-neutral-200 bg-neutral-50/70' : 'border-neutral-800 bg-neutral-950/50'}`}>
                <div className="flex items-center gap-2">
                  <Shield size={15} className={isLight ? 'text-indigo-500' : 'text-indigo-300'} />
                  <h3 className={`text-sm font-semibold ${title}`}>Risk</h3>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <label className={labelClass(isLight)}>Risk</label>
                    <select
                      className={fieldClass(isLight)}
                      value={form.riskLevel}
                      onChange={(e) => setForm((p) => ({ ...p, riskLevel: e.target.value as PublishedCapability['riskLevel'] }))}
                    >
                      {RISK_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelClass(isLight)}>Rate limit / min</label>
                    <input
                      type="number"
                      className={fieldClass(isLight)}
                      value={form.rateLimitPerMinute}
                      onChange={(e) => setForm((p) => ({ ...p, rateLimitPerMinute: Number(e.target.value) || 0 }))}
                    />
                  </div>
                  <div>
                    <label className={labelClass(isLight)}>Max records</label>
                    <input
                      type="number"
                      className={fieldClass(isLight)}
                      value={form.maxRecords}
                      onChange={(e) => setForm((p) => ({ ...p, maxRecords: Number(e.target.value) || 0 }))}
                    />
                  </div>
                </div>
                <div className={`rounded-xl border px-4 divide-y ${isLight ? 'border-neutral-200 bg-white divide-neutral-100' : 'border-neutral-800 bg-neutral-900 divide-neutral-800'}`}>
                  <ToggleRow
                    label="Prompt-injection protection"
                    checked={form.promptInjectionProtection}
                    onChange={(promptInjectionProtection) => setForm((p) => ({ ...p, promptInjectionProtection }))}
                    isLight={isLight}
                  />
                  <ToggleRow
                    label="PII detection"
                    checked={form.piiDetection}
                    onChange={(piiDetection) => setForm((p) => ({ ...p, piiDetection }))}
                    isLight={isLight}
                  />
                  <ToggleRow
                    label="Secrets / credential leakage"
                    checked={form.secretsLeakageProtection}
                    onChange={(secretsLeakageProtection) => setForm((p) => ({ ...p, secretsLeakageProtection }))}
                    isLight={isLight}
                  />
                  <ToggleRow
                    label="Jailbreak / instruction-override"
                    checked={form.jailbreakProtection}
                    onChange={(jailbreakProtection) => setForm((p) => ({ ...p, jailbreakProtection }))}
                    isLight={isLight}
                  />
                  <ToggleRow
                    label="Output governance"
                    checked={form.outputGovernance}
                    onChange={(outputGovernance) => setForm((p) => ({ ...p, outputGovernance }))}
                    isLight={isLight}
                  />
                  <ToggleRow
                    label="Mask / redact sensitive fields"
                    checked={form.maskingEnabled}
                    onChange={(maskingEnabled) => setForm((p) => ({ ...p, maskingEnabled }))}
                    isLight={isLight}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>
        </div>
        <div className={`hidden lg:block min-h-0 overflow-y-auto ${isLight ? 'border-l border-neutral-100' : 'border-l border-white/10'}`}>
          <GuideSidebar isLight={isLight} />
        </div>
      </div>
    </GatewayDrawer>
    {showIngestionModal
      ? createPortal(
          <IngestionEntityModal
            overlayClassName="z-[200]"
            onClose={() => {
              setShowIngestionModal(false);
              setCreateError(null);
              logGovernedMcp('app', 'publish_add_data_model_dismissed');
            }}
            onSubmit={handleCreateDataModel}
            submitting={creatingModel}
            submitError={createError}
          />,
          document.body
        )
      : null}
    <AssetExplorerModal
      open={showAssetExplorer}
      catalog={assetCatalog}
      selectedAssetId={selectedAsset?.id || form.selectedTableIds[0] || ''}
      isLight={isLight}
      onClose={() => setShowAssetExplorer(false)}
      onSelect={applySelectedAsset}
    />
    <ModelAssetPickerModal
      open={showAssetPicker}
      assets={modelAssets}
      selectedIds={form.selectedTableIds}
      modelName={activeModel?.name || form.sourceName}
      isLight={isLight}
      onClose={() => setShowAssetPicker(false)}
      onApply={(ids) => {
        setForm((prev) => ({
          ...prev,
          selectedTableIds: ids,
          publishScope: 'data_model',
          assetKind: '',
          assetName: '',
        }));
        setShowAssetPicker(false);
        setError(null);
      }}
    />
    <KnowledgeBasePickerModal
      open={showKnowledgePicker}
      selectedIds={selectedKnowledgeBaseIds}
      knownBases={[...knowledgeBases, ...pickedKnowledgeBases, ...selectedKnowledgeBases]}
      isLight={isLight}
      onClose={() => setShowKnowledgePicker(false)}
      onApply={applyKnowledgeBases}
    />
    <WorkflowPickerModal
      open={showWorkflowPicker}
      selectedId={form.sourceId}
      isLight={isLight}
      onClose={() => setShowWorkflowPicker(false)}
      onApply={(workflow) => {
        void applyWorkflow(workflow);
      }}
    />
    </>
  );
}
